import React, { useContext, useEffect, useState } from 'react';
import LazyLoad from 'react-lazyload';
import MediaPreloader from '@com/core/common/components/mediaPreloader';
import { isExternalLink } from '@com/core/common/utils/url';
import { CustomerContext } from '@com/core/common/contexts/customerContext';
import styled from 'styled-components';
import { colors } from '@com/core/noon/styles/noonTheme';
import { TSupportLinkType } from '@com/core/common/types/catalog';
import { TTrackClick } from '@com/core/common/types/tracking';
import Link from '@com/core/common/components/Link';
import { Router } from '@com/core/common/translations/i18n';
import ExperienceSwitch from '../../../common/ExperienceSwitch';

const commonSpace = 12;

const Banner = styled.div`
    position: relative;
    display: block;
`;

const TextContainer = styled.div`
    margin-top: 12px;

    .featureBanner & {
        margin-top: 16px;
    }

    /* This probably doesn't work */
    /* .bannerModuleOverlay &,
    .bgImageBanner & {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        display: flex;
        justify-content: center;
        align-items: center;
        margin: 0;
        color: #fff;
    } */
`;

const Title = styled.p`
    font-weight: 600;
    font-size: 1.25rem;

    .featureBanner & {
        font-weight: bold;
        font-size: 2em;
    }
`;

const Summary = styled.p`
    margin-top: 2px;
    font-size: 0.9375rem;

    .featureBanner & {
        font-size: 1.125rem;
    }
`;

const BgImageBanner = styled(Banner)`
    height: 100%;
    background-size: cover;
    background-position: center center;
`;

const Cta = styled.span<{ bgColor?: string }>`
    font-weight: bold;
    font-size: 0.916666rem;
    text-transform: uppercase;
    letter-spacing: 0.2px;
    padding: ${commonSpace / 2}px ${commonSpace * 2}px;
    border: 1px solid;
    background-color: ${({ bgColor }: { bgColor?: string }): string => bgColor || colors.bgPrimary};
    display: inline-block;
`;

// SUPPORT LINKS THAT GO UNDER BANNER
const SupportLinks = styled.ul<{ color?: string }>`
    margin-top: 6px;
    color: ${({ color }: { color?: string }): string => color || colors.bodyMid};
`;

type TSupportLinkProps = {
    emphasis?: boolean;
    color?: string;
};

const SupportLink = styled.li<TSupportLinkProps>`
    a {
        padding: 10px 0;
        display: block;

        ${({ color, emphasis }: TSupportLinkProps): string =>
            emphasis
                ? `
            color: ${color || colors.brandSecondary};
            font-weight: bold;
            text-transform: uppercase;
            font-size: 1rem;
            letter-spacing: 0.2px;
            padding-top: 14px;`
                : ''}
    }

    &:last-child a {
        padding-bottom: 0;
    }
`;

const NavTo = styled.button`
    display: contents;
`;

type TProps = {
    bannerStyle?: string;
    bannerData: {
        linkUrl?: string;
        imageUrl?: string;
        ratio?: number;
        titleText?: string;
        summaryText?: string;
        supportLinks?: TSupportLinkType[];
        videoUrl?: string;
        videoRatio?: string;
        videoControls?: boolean;
        ctaText?: string;
        ctaBgColor?: string;
        ctaColor?: string;
        gapt?: TTrackClick; // { name: string; position: string };
    };
};
const BannerContent = ({ bannerData, bannerStyle }: TProps): JSX.Element => {
    const {
        ctaBgColor,
        ctaColor,
        ctaText,
        gapt,
        imageUrl,
        linkUrl,
        ratio,
        summaryText,
        supportLinks,
        titleText,
        videoControls,
        videoUrl,
    } = bannerData;

    const imagePlaceholder = <div style={{ paddingBottom: `${ratio || 0}%` }} />;
    const ctaStyles = {
        ...(ctaBgColor && { backgroundColor: ctaBgColor }),
        ...(ctaColor && { color: ctaColor }),
    };

    const { customer, setInitialLoginScreen, showLoginModal } = useContext(CustomerContext);
    const [isToNavigate, setIsToNavigate] = useState(false);

    useEffect(() => {
        if (isToNavigate && linkUrl && customer) {
            Router.push(linkUrl);
        }
    }, [isToNavigate, customer]);

    const bannerElement = (): JSX.Element => (
        <Banner className={bannerStyle}>
            {!!videoUrl && (
                <LazyLoad once placeholder={imagePlaceholder} offset={300}>
                    <MediaPreloader ratio={ratio}>
                        {videoControls ? (
                            <video controls>
                                <track default kind="captions" />
                                <source src={videoUrl} type="video/mp4" />
                            </video>
                        ) : (
                            <video autoPlay loop muted playsInline>
                                <source src={videoUrl} type="video/mp4" />
                            </video>
                        )}
                    </MediaPreloader>
                </LazyLoad>
            )}
            {!!imageUrl && (
                <LazyLoad once placeholder={imagePlaceholder} offset={300}>
                    <MediaPreloader ratio={ratio}>
                        <img src={imageUrl} alt={linkUrl} />
                    </MediaPreloader>
                </LazyLoad>
            )}
            {titleText && (
                <TextContainer>
                    <Title>{titleText}</Title>
                    {summaryText && <Summary>{summaryText}</Summary>}
                </TextContainer>
            )}
            {ctaText && <Cta style={ctaStyles}>{ctaText}</Cta>}
        </Banner>
    );

    const navToLogin = () => {
        showLoginModal(true);
        setInitialLoginScreen('login');
        setIsToNavigate(true);
    };

    const bannerElementWrapper = (banner: JSX.Element): JSX.Element => {
        // this is basically for that one random cms banner that comes from the cart response for daily GMM
        if (linkUrl === 'daily-modal://experience-switch') {
            return <ExperienceSwitch>{banner}</ExperienceSwitch>;
        }

        if (!linkUrl) {
            return banner;
        }

        if (isExternalLink(linkUrl)) {
            return (
                <a href={linkUrl} target="_blank" rel="noopener noreferrer" title="Opens in new window">
                    {banner}
                </a>
            );
        }

        if (!customer && linkUrl.includes('monthly-spendings')) {
            return (
                <NavTo type="button" onClick={() => navToLogin()}>
                    {banner}
                </NavTo>
            );
        }

        return (
            <Link href={linkUrl} analytics={gapt ? { ...gapt, id: gapt.name } : {}}>
                {banner}
            </Link>
        );
    };

    if (bannerStyle === 'bgImage' && linkUrl && isExternalLink(linkUrl)) {
        return (
            <BgImageBanner
                href={linkUrl}
                as="a"
                style={{ backgroundImage: `url(${imageUrl})` }}
                target="_blank"
                title="Background Image External Link"
                rel="noopener noreferrer">
                {titleText && (
                    <TextContainer>
                        <Title>{titleText}</Title>
                        {summaryText && <Summary>{summaryText}</Summary>}
                    </TextContainer>
                )}
                {ctaText && <Cta style={ctaStyles}>{ctaText}</Cta>}
            </BgImageBanner>
        );
    }

    if (bannerStyle === 'bgImage' && linkUrl && !isExternalLink(linkUrl)) {
        return (
            <Link href={linkUrl}>
                <BgImageBanner style={{ backgroundImage: `url(${imageUrl})` }} title="Background banner Link">
                    {titleText && (
                        <TextContainer>
                            <Title>{titleText}</Title>
                            {summaryText && <Summary>{summaryText}</Summary>}
                        </TextContainer>
                    )}
                    {ctaText && <Cta style={ctaStyles}>{ctaText}</Cta>}
                </BgImageBanner>
            </Link>
        );
    }

    return (
        <div>
            {bannerElementWrapper(bannerElement())}
            {supportLinks && (
                <SupportLinks>
                    {supportLinks.map(link => (
                        <SupportLink key={link.linkUrl} className={link.emphasis ? 'emphasis' : ''}>
                            <Link href={link.linkUrl}>{link.linkText}</Link>
                        </SupportLink>
                    ))}
                </SupportLinks>
            )}
        </div>
    );
};

export default BannerContent;
